import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../features/auth/AuthContext";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import { getTeacherSetup, saveTeacherSetup } from "./meetingService";
import TimePickerDropdown from "./TimePickerDropdown";
import "./bookAMeeting.css";

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      // Convert to 12-hour format
      let displayHour = hour;
      if (hour === 0) displayHour = 12;
      else if (hour > 12) displayHour = hour - 12;
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayTime = `${displayHour}:${String(minute).padStart(2, "0")} ${ampm}`;
      slots.push({ value: timeStr, label: displayTime });
    }
  }
  return slots;
};


const TIME_SLOTS = generateTimeSlots();
const BUFFER_MIN = 0;
const BUFFER_MAX = 60;
const MAX_SESSIONS = 3;

const convertTo12Hour = (timeStr) => {
  if (!timeStr) return "";
  const [hour, minute] = timeStr.split(":");
  let h = parseInt(hour, 10);
  const m = minute;
  const ampm = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m}${ampm}`;
};

const getMinimumPriceForCurrency = (symbol) => (symbol === "₹" ? 100 : 1);

const OneOnOneSession = ({ role, isMeetingEnabled = true, onMeetingEnabledChange }) => {
  const isTeacher = role === "Teacher";
  const { token } = useAuth();
  const globalState = useSelector((s) => s.globalState);

  const [minimumPrice, setMinimumPrice] = useState(1);
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const [sessions, setSessions] = useState([]);
  const [buffer, setBuffer] = useState({ before: 0, after: 0 });
  const [bufferErrors, setBufferErrors] = useState({ before: false, after: false });
  const [timePickerErrors, setTimePickerErrors] = useState(new Set());
  const [sessionErrors, setSessionErrors] = useState({});
  const [availabilityError, setAvailabilityError] = useState(""); // string: empty if no error, message if error
  const [availability, setAvailability] = useState({
    mode: "set-by-day",
    slots: DAYS.reduce(
      (acc, day, idx) => ({
        ...acc,
        [day]: {
          enabled: idx !== 0 && idx !== 6,
          timeSlots: [{ id: 1, start: "09:00", end: "17:00" }],
        },
      }),
      {},
    ),
    everyDay: {
      selectedDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      timeSlots: [{ id: 1, start: "09:00", end: "17:00" }],
    },
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Store original/last saved state for quick cancel
  const [savedState, setSavedState] = useState(null);

  // Scroll target for the Availability section, used when validation fails
  const availabilitySectionRef = useRef(null);


  const parseSetupData = (data) => {
    if (data.offerings && Array.isArray(data.offerings) && data.offerings.length > 0) {
      // Returning teacher — use the currency already attached to their saved offerings
      const parsedSessions = data.offerings.map((offering, idx) => ({
        id: idx + 1,
        duration: offering.duration_minutes,
        price: parseFloat(offering.price) || 0,
        currency_symbol: offering.currency_symbol || "$",
      }));
      setSessions(parsedSessions);

      const symbol = parsedSessions[0].currency_symbol;
      setCurrencySymbol(symbol);
      setMinimumPrice(getMinimumPriceForCurrency(symbol));
    } else {
      // First-time teacher — no offerings yet, keep form empty
      // User will explicitly add sessions via "+ Add Session" button
      const symbol = data.currency_symbol || "$";
      setCurrencySymbol(symbol);
      setMinimumPrice(getMinimumPriceForCurrency(symbol));
      setSessions([]);
    }

          if (data.settings) {
            setBuffer({
              before: data.settings.pre_buffer_minutes || 0,
              after: data.settings.post_buffer_minutes || 0,
            });
          }

          if (data.availability && data.availability.days) {
            const newSlots = DAYS.reduce(
              (acc, day, idx) => {
                const dayData = data.availability.days.find((d) => d.weekday === idx);
                return {
                  ...acc,
                  [day]: {
                    enabled: dayData?.is_available ?? false,
                    timeSlots: (dayData?.slots || []).map((slot, slotIdx) => ({
                      id: slotIdx + 1,
                      start: slot.start_time?.substring(0, 5) || "09:00",
                      end: slot.end_time?.substring(0, 5) || "17:00",
                    })),
                  },
                };
              },
              {}
            );
            setAvailability((prevAvail) => ({
              ...prevAvail,
              slots: newSlots,
              mode: "set-by-day",
            }));
          }
        };

  // Handle when isMeetingEnabled changes
  useEffect(() => {
    if (!isMeetingEnabled) {
      setIsEditing(false);
      setError("");
      setSuccess("");
      // Revert to saved state when meetings are disabled
      if (savedState) {
        setSessions(savedState.sessions);
        setBuffer(savedState.buffer);
        setAvailability(savedState.availability);
        setMinimumPrice(savedState.minimumPrice);
        setCurrencySymbol(savedState.currencySymbol);
      }
    }
  }, [isMeetingEnabled, savedState]);

  // Add initial empty session row when entering edit mode with no sessions
  useEffect(() => {
    if (isEditing && sessions.length === 0) {
      setSessions([{
        id: 1,
        duration: null,
        price: minimumPrice,
        currency_symbol: currencySymbol,
      }]);
    }
  }, [isEditing]);

  // Save current state when loading completes
  useEffect(() => {
    if (!loading) {
      setSavedState({
        sessions,
        buffer,
        availability,
        minimumPrice,
        currencySymbol,
      });
    }
  }, [loading]);

  // Fetch setup data on mount
  useEffect(() => {
    const fetchSetup = async () => {
      try {
        setLoading(true);
        const res = await getTeacherSetup(token);
        if (res.ok && res.data) {
          parseSetupData(res.data);
        } else if (!res.ok) {
          console.error("Failed to fetch setup:", res.error);
        }
      } catch (err) {
        console.error("Error fetching setup:", err);
      } finally {
        setLoading(false);
      }
    };

    if (isTeacher && token) {
      fetchSetup();
    }
  }, [isTeacher, token]);
  const handleAddSession = () => {
    setSessions([
      ...sessions,
      {
        id: Math.max(...sessions.map((s) => s.id), 0) + 1,
        duration: null,
        price: minimumPrice,
        currency_symbol: currencySymbol,
      },
    ]);
  };

  const handleRemoveSession = (id) => {
    if (sessions.length > 1) {
      setSessions(sessions.filter((s) => s.id !== id));
    }
  };

  const handleSessionChange = (id, field, value) => {
    setSessions(
      sessions.map((s) =>
        s.id === id
          ? {
              ...s,
              [field]:
                field === "duration" || field === "price"
                  ? parseFloat(value)
                  : value,
            }
          : s,
      ),
    );
    // Clear field error when user edits
    setSessionErrors((prev) => {
      if (!prev[id]?.[field]) return prev;
      return { ...prev, [id]: { ...prev[id], [field]: undefined } };
    });
  };

  const handleBufferChange = (field, rawValue) => {
    if (rawValue === "") {
      setBuffer((prev) => ({ ...prev, [field]: "" }));
      setBufferErrors((prev) => ({ ...prev, [field]: false }));
      return;
    }

    const parsed = Number.parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) return;

    const isInvalid = parsed > BUFFER_MAX;
    setBufferErrors((prev) => ({ ...prev, [field]: isInvalid }));

    if (!isInvalid) {
      setBuffer((prev) => ({
        ...prev,
        [field]: Math.min(Math.max(parsed, BUFFER_MIN), BUFFER_MAX),
      }));
    } else {
      setBuffer((prev) => ({
        ...prev,
        [field]: parsed,
      }));
    }
  };

  const handleBufferBlur = (field) => {
    setBuffer((prev) => ({
      ...prev,
      [field]: prev[field] === "" ? 0 : prev[field],
    }));
  };

  const handleAvailabilityChange = (day, field, value) => {
    const updatedDay = { ...availability.slots[day], [field]: value };

    if (field === "enabled") {
      setAvailabilityError("");
      if (value === true) {
        // When enabling, reset to default single slot
        updatedDay.timeSlots = [{ id: 1, start: "09:00", end: "17:00" }];
      } else {
        // When disabling, clear time slots and remove error keys for this day
        updatedDay.timeSlots = [];
        setTimePickerErrors((prev) => {
          const newErrors = new Set(prev);
          // Remove all error keys for this day
          [...newErrors].forEach((key) => {
            if (key.startsWith(`${day}-`)) {
              newErrors.delete(key);
            }
          });
          return newErrors;
        });
      }
    }

    setAvailability({
      ...availability,
      slots: {
        ...availability.slots,
        [day]: updatedDay,
      },
    });
  };

  const handleTimeSlotChange = (day, slotId, field, value) => {
    const daySlots = availability.slots[day].timeSlots;
    const updatedSlots = daySlots.map((slot) =>
      slot.id === slotId ? { ...slot, [field]: value } : slot,
    );
    setAvailability({
      ...availability,
      slots: {
        ...availability.slots,
        [day]: { ...availability.slots[day], timeSlots: updatedSlots },
      },
    });
  };

  const handleAddTimeSlot = (day) => {
    const daySlots = availability.slots[day].timeSlots;
    const newId = Math.max(...daySlots.map((s) => s.id), 0) + 1;
    setAvailability({
      ...availability,
      slots: {
        ...availability.slots,
        [day]: {
          ...availability.slots[day],
          timeSlots: [...daySlots, { id: newId, start: "09:00", end: "17:00" }],
        },
      },
    });
  };

  const handleRemoveTimeSlot = (day, slotId) => {
    const daySlots = availability.slots[day].timeSlots;
    if (daySlots.length > 1) {
      setAvailability({
        ...availability,
        slots: {
          ...availability.slots,
          [day]: {
            ...availability.slots[day],
            timeSlots: daySlots.filter((slot) => slot.id !== slotId),
          },
        },
      });
      // Clean up error keys for removed slot
      setTimePickerErrors((prev) => {
        const newErrors = new Set(prev);
        newErrors.delete(`${day}-${slotId}-start`);
        newErrors.delete(`${day}-${slotId}-end`);
        return newErrors;
      });
    }
  };

  const handleTimePickerError = (slotKey, hasError) => {
    setTimePickerErrors((prev) => {
      const newErrors = new Set(prev);
      if (hasError) {
        newErrors.add(slotKey);
      } else {
        newErrors.delete(slotKey);
      }
      return newErrors;
    });
  };

  const handleSave = async () => {
    // Check for time picker errors
    if (timePickerErrors.size > 0) {
      toast.error("Please fix all time format errors before saving");
      return;
    }

    let hasErrors = false;
    const newSessionErrors = {};
    let newAvailabilityError = ""; // message, or empty if no error

    // Validate all sessions
    sessions.forEach((session) => {
      const sessionId = session.id;
      newSessionErrors[sessionId] = {};

      // Validate duration
      if (!session.duration || session.duration <= 0) {
        newSessionErrors[sessionId].duration = "Please select a duration";
        hasErrors = true;
      }

      // Validate price if marketplace is enabled
      const isMarketplaceEnabled = globalState?.enabled_modules?.includes("marketplace");
      if (isMarketplaceEnabled) {
        if (!session.price || session.price < minimumPrice) {
          newSessionErrors[sessionId].price = `Price must be at least ${currencySymbol}${minimumPrice}`;
          hasErrors = true;
        }
      }

      // Clean up empty error objects
      if (Object.keys(newSessionErrors[sessionId]).length === 0) {
        delete newSessionErrors[sessionId];
      }
    });

    // Toast for session errors (if any)
    if (Object.keys(newSessionErrors).length > 0) {
      const firstError = Object.values(newSessionErrors)[0];
      toast.error(Object.values(firstError)[0]);
    }

    // Validate buffer time
    const bufferBefore = Number(buffer.before) || 0;
    const bufferAfter = Number(buffer.after) || 0;
    if (bufferBefore > BUFFER_MAX || bufferAfter > BUFFER_MAX) {
      setBufferErrors({
        before: bufferBefore > BUFFER_MAX,
        after: bufferAfter > BUFFER_MAX,
      });
      toast.error("Buffer time cannot exceed 60 minutes");
      hasErrors = true;
    }

    // Validate availability based on mode
    if (availability.mode === "every-day") {
      // Validate every-day mode: at least one day selected and has valid time slots
      if (availability.everyDay.selectedDays.length === 0) {
        newAvailabilityError = "Please select at least one day.";
        toast.error(newAvailabilityError);
        hasErrors = true;
      } else {
        for (const slot of availability.everyDay.timeSlots) {
          if (!slot.start || !slot.end) {
            newAvailabilityError = "Please fill in all time slots.";
            toast.error(newAvailabilityError);
            hasErrors = true;
            break;
          }
          if (slot.start >= slot.end) {
            newAvailabilityError = "End time must be after start time.";
            toast.error(newAvailabilityError);
            hasErrors = true;
            break;
          }
        }
        // Check for overlapping slots
        if (!hasErrors && findOverlap(availability.everyDay.timeSlots)) {
          newAvailabilityError = "Some time slots overlap — please adjust them.";
          toast.error(newAvailabilityError);
          hasErrors = true;
        }
      }
    } else {
      // Validate set-by-day mode: at least one day enabled with valid time slots
      const hasEnabledDay = DAYS.some((day) => availability.slots[day].enabled);
      if (!hasEnabledDay) {
        newAvailabilityError = "Please enable at least one day.";
        toast.error(newAvailabilityError);
        hasErrors = true;
      } else {
        for (const day of DAYS) {
          const daySlots = availability.slots[day];
          if (daySlots.enabled) {
            for (const slot of daySlots.timeSlots) {
              if (!slot.start || !slot.end) {
                newAvailabilityError = `Please fill in all times for ${day}.`;
                toast.error(newAvailabilityError);
                hasErrors = true;
                break;
              }
              if (slot.start >= slot.end) {
                newAvailabilityError = `End time must be after start time for ${day}.`;
                toast.error(newAvailabilityError);
                hasErrors = true;
                break;
              }
            }
            // Check for overlapping slots on this day
            if (!hasErrors && findOverlap(daySlots.timeSlots)) {
              newAvailabilityError = `Time slots for ${day} overlap — please adjust them.`;
              toast.error(newAvailabilityError);
              hasErrors = true;
              break;
            }
            if (hasErrors) break;
          }
        }
      }
    }

    // Set error states
    setSessionErrors(newSessionErrors);
    setAvailabilityError(newAvailabilityError);

    // Bring the Availability section into view if that's what failed
    if (newAvailabilityError) {
      availabilitySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    // Stop if there are any errors
    if (hasErrors) {
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      // Format availability: convert "every-day" mode to "set-by-day" format
      let availabilityDays;

      if (availability.mode === "every-day") {
        // Convert selected days with shared time slots to set-by-day format
        availabilityDays = DAYS.map((day, idx) => {
          const isSelected = availability.everyDay.selectedDays.includes(day);
          return {
            weekday: idx,
            slots: isSelected
              ? availability.everyDay.timeSlots.map((slot) => ({
                  start_time: slot.start,
                  end_time: slot.end,
                }))
              : [],
          };
        });
      } else {
        // Use set-by-day format directly
        availabilityDays = DAYS.map((day, idx) => {
          const daySlots = availability.slots[day];
          return {
            weekday: idx,
            slots: daySlots.enabled
              ? daySlots.timeSlots.map((slot) => ({
                  start_time: slot.start,
                  end_time: slot.end,
                }))
              : [],
          };
        });
      }

      // Keep `.slots` in sync with whatever was just saved, regardless of which tab (Set by Day / Same Everyday) produced it
      const syncedSlots = DAYS.reduce((acc, day, idx) => {
        const dayPayload = availabilityDays[idx];
        return {
          ...acc,
          [day]: {
            enabled: dayPayload.slots.length > 0,
            timeSlots: dayPayload.slots.map((slot, i) => ({
              id: i + 1,
              start: slot.start_time,
              end: slot.end_time,
            })),
          },
        };
      }, {});

      const updatedAvailability = { ...availability, slots: syncedSlots };

      // Format payload for PUT /students/meet/teacher/setup/
      const isMarketplaceEnabled = globalState?.enabled_modules?.includes("marketplace");
      const payload = {
        settings: {
          pre_buffer_minutes: Number(buffer.before) || 0,
          post_buffer_minutes: Number(buffer.after) || 0,
        },
        offerings: sessions.map((s) => ({
          duration_minutes: s.duration,
          price: isMarketplaceEnabled ? String(s.price) : null,
        })),
        availability: {
          days: availabilityDays,
        },
      };

      const res = await saveTeacherSetup(token, payload);

      if (res.ok) {
        const successMsg = res.message || "Session settings saved successfully!";
        toast.success(successMsg);
        setAvailability(updatedAvailability);
        setSavedState({
          sessions,
          buffer,
          availability: updatedAvailability,
          minimumPrice,
          currencySymbol,
        });
        setIsEditing(false);
      } else {
        // Show specific validation errors from backend (handles flat strings,
        // arrays, and nested DRF-style dicts like { availability: { non_field_errors: [...] } })
        let allErrors = [];
        let toastMessage = res.error || "Failed to save settings";

        if (res.data?.errors) {
          allErrors = flattenErrorMessages(res.data.errors);

          if (allErrors.length > 0) {
            setError(allErrors.join("\n\n"));
            toastMessage = allErrors[0];
          }
        }

        if (allErrors.length === 0 && res.data?.message) {
          toastMessage = res.data.message;
        }

        toast.error(toastMessage);
      }
    } catch (err) {
      const errorMsg = err.message || "An error occurred";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const flattenErrorMessages = (errors) => {
    if (!errors) return [];
    if (typeof errors === "string") return [errors];
    if (Array.isArray(errors)) {
      return errors.flatMap((e) => flattenErrorMessages(e));
    }
    if (typeof errors === "object") {
      return Object.values(errors).flatMap((e) => flattenErrorMessages(e));
    }
    return [];
  };

  const handleCancel = () => {
    setError("");
    setSuccess("");
    if (savedState) {
      setSessions(savedState.sessions);
      setBuffer(savedState.buffer);
      setAvailability(savedState.availability);
      setMinimumPrice(savedState.minimumPrice);
      setCurrencySymbol(savedState.currencySymbol);
    }
    setIsEditing(false);
  };

  const getTimeSlotErrorState = (slot, siblingSlots = []) => {
    if (!slot.start || !slot.end || slot.start >= slot.end) {
      return { hasError: true };
    }
    // Check for overlap with sibling slots in the same day
    const overlaps = siblingSlots.some((other) => {
      if (other.id === slot.id) return false;
      if (!other.start || !other.end || other.start >= other.end) return false;
      return slot.start < other.end && other.start < slot.end;
    });
    return overlaps ? { hasError: true, overlap: true } : { hasError: false };
  };

  const findOverlap = (slots) => {
    const valid = slots.filter((s) => s.start && s.end && s.start < s.end);
    for (let i = 0; i < valid.length; i++) {
      for (let j = i + 1; j < valid.length; j++) {
        if (valid[i].start < valid[j].end && valid[j].start < valid[i].end) {
          return true;
        }
      }
    }
    return false;
  };

  const handleToggleDayForEveryDay = (day) => {
    setAvailabilityError(false);
    setAvailability({
      ...availability,
      everyDay: {
        ...availability.everyDay,
        selectedDays: availability.everyDay.selectedDays.includes(day)
          ? availability.everyDay.selectedDays.filter((d) => d !== day)
          : [...availability.everyDay.selectedDays, day],
      },
    });
  };

  const handleEveryDayTimeSlotChange = (slotId, field, value) => {
    const updatedSlots = availability.everyDay.timeSlots.map((slot) =>
      slot.id === slotId ? { ...slot, [field]: value } : slot,
    );
    setAvailability({
      ...availability,
      everyDay: { ...availability.everyDay, timeSlots: updatedSlots },
    });
  };

  const handleAddEveryDayTimeSlot = () => {
    const newId =
      Math.max(...availability.everyDay.timeSlots.map((s) => s.id), 0) + 1;
    setAvailability({
      ...availability,
      everyDay: {
        ...availability.everyDay,
        timeSlots: [
          ...availability.everyDay.timeSlots,
          { id: newId, start: "09:00", end: "17:00" },
        ],
      },
    });
  };

  const handleRemoveEveryDayTimeSlot = (slotId) => {
    if (availability.everyDay.timeSlots.length > 1) {
      setAvailability({
        ...availability,
        everyDay: {
          ...availability.everyDay,
          timeSlots: availability.everyDay.timeSlots.filter(
            (slot) => slot.id !== slotId,
          ),
        },
      });
      // Clean up error keys for removed slot
      setTimePickerErrors((prev) => {
        const newErrors = new Set(prev);
        newErrors.delete(`everyday-${slotId}-start`);
        newErrors.delete(`everyday-${slotId}-end`);
        return newErrors;
      });
    }
  };

  const handleKeyDownBuffer = (e) => {
    if (["-", "+", "e", "E"].includes(e.key)) {
      e.preventDefault();
    }
  };


  if (!isTeacher) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px 0",
          color: "var(--bm-muted)",
        }}
      >
        Only teachers can manage 1 on 1 sessions.
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px 0",
          color: "var(--bm-muted)",
        }}
      >
        Loading session settings...
      </div>
    );
  }

  const platformFee = 0.3;
  const totalSessions = sessions.reduce((sum, s) => sum + s.price, 0);
  const totalEarnings = totalSessions * (1 - platformFee);

  return (
    <div className="bm-oneone-container">
      {success && <div className="bm-success-banner">{success}</div>}
      {/* {error && <div className="bm-error-banner">{error}</div>} */}

      {/* Session Pricing */}
      <div className="bm-section">
        <div className="mb-2">
        <div className="bm-section-header">
          <h3 className="bm-section-title">1 on 1 Session</h3>
          <div className="bm-actions mt-0">
            {!isEditing ? (
              <>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                  disabled={!isMeetingEnabled}
                  data-tooltip-id="edit-btn-tooltip"
                className="bm-outline-btn"
                  style={{ color: !isMeetingEnabled ?  "#c5c9d1" : "#0068ff", cursor: !isMeetingEnabled ? "default" : "pointer", border: !isMeetingEnabled ?  "1px solid #c5c9d1" : "1px solid #0068ff"}}
              >
                Edit
              </button>
                {!isMeetingEnabled && (
                  <Tooltip
                    id="edit-btn-tooltip"
                    content="Enable meetings first to edit session configuration"
                    place="top"
                    delayShow={0}
                    className="bm-tooltip"
                  />
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bm-outline-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="bm-primary-btn"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
        <p className="bm-section-desc">
          Invitees can schedule 30 days into the future with at least 2 hours
          notice
        </p>
        </div>

        <div className="bm-sessions-list">
          {sessions.map((session, idx) => (
            <div key={session.id} className="bm-session-row">
              <div className="bm-session-field">
                <label className="bm-field-label">
                  Duration
                </label>
                {isEditing ? (
                  <>
                    <select
                      value={session.duration || ""}
                      onChange={(e) =>
                        handleSessionChange(
                          session.id,
                          "duration",
                          e.target.value,
                        )
                      }
                      className={`bm-select ${sessionErrors[session.id]?.duration ? "bm-field-error" : ""}`}
                    >
                      <option value="">Select Duration</option>
                      <option value="30">30 Min</option>
                      <option value="60">60 Min</option>
                      <option value="90">90 Min</option>
                    </select>
                    {sessionErrors[session.id]?.duration && (
                      <div className="bm-field-error-message">
                        <i className="fa-solid fa-circle-exclamation"></i>
                        {sessionErrors[session.id].duration}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bm-field-display">{session.duration} Min</div>
                )}
              </div>

              {globalState?.enabled_modules?.includes("marketplace") && (
              <>
              <div className="bm-session-field">
                <label className="bm-field-label">Session Price</label>
                {isEditing ? (
                  <>
                    <div className={`bm-price-input-wrap ${sessionErrors[session.id]?.price ? "bm-field-error" : ""}`}>
                      <span className="bm-price-currency">{session.currency_symbol}</span>
                      <input
                        type="number"
                        step="0.01"
                        min={minimumPrice}
                        value={session.price}
                        onChange={(e) =>
                          handleSessionChange(session.id, "price", e.target.value)
                        }
                        className="bm-price-input"
                        placeholder="0.00"
                      />
                    </div>
                    {sessionErrors[session.id]?.price && (
                      <div className="bm-field-error-message">
                        <i className="fa-solid fa-circle-exclamation"></i>
                        {sessionErrors[session.id].price}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bm-field-display">{session.currency_symbol}{session.price || 0}</div>
                )}
              </div>

              <div className="bm-session-field">
                <label className="bm-field-label">You Get</label>
                <div className="bm-you-get">
                  {session.currency_symbol}{((session.price || 0) * (1 - platformFee)).toFixed(2)}
                  <span className="bm-fee-info">
                    after {session.currency_symbol}{((session.price || 0) * platformFee).toFixed(2)} Platform fee (30%)
                  </span>
                </div>
              </div>
              </>

              )}

              {sessions.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveSession(session.id)}
                  className="bm-remove-session-btn"
                  aria-label="Remove session"
                  disabled={!isEditing}
                >
                  <i className="fa-solid fa-trash-can" />
                </button>
              )}
            </div>
          ))}
        </div>

        {isEditing && sessions.length < MAX_SESSIONS && (
          <button
            type="button"
            onClick={handleAddSession}
            className="bm-add-session-link"
          >
            + Add Session
          </button>
        )}
        {globalState?.enabled_modules?.includes("marketplace") && sessions.length > 0 && (
        <p className="bm-platform-fee-note">
          Platform fee is 30% of each session price.
        </p>
        )}
      </div>

      {/* Buffer Time */}
      <div className="bm-section" style={{borderTop: "1px solid var(--bm-border)", paddingTop: "15px"}}>
        <h3 className="bm-section-title">Buffer</h3>
        <div className="bm-buffer-row">
          {isEditing ? (
            <div className="bm-buffer-field">
              <label className="bm-field-label">
                Before meeting (Prep time)
                {bufferErrors.before && <span style={{ color: "var(--bm-red)", marginLeft: "6px", fontSize: "0.85em" }}>Max 60 minutes</span>}
              </label>

              <div className="bm-buffer-input-wrap">
                <input
                  type="number"
                  inputMode="numeric"
                  min={BUFFER_MIN}
                  step={5}
                  value={buffer.before}
                  onChange={(e) => handleBufferChange("before", e.target.value)}
                  onBlur={() => handleBufferBlur("before")}
                  onKeyDown={handleKeyDownBuffer}
                  onWheel={(e) => e.currentTarget.blur()}
                  className={`bm-select ${bufferErrors.before ? "bm-input-error" : ""}`}
                  placeholder="0"
                />
                <span className="bm-buffer-unit">Min</span>
              </div>
            </div>
          ) : (
            <div className="bm-field-display">{buffer.before} min{buffer.before === 1 ? "" : "s"} before meeting (prep time)</div>
          )}

          {isEditing ? (
            <div className="bm-buffer-field">
              <label className="bm-field-label">
                After meeting (Wrap-up)
                {bufferErrors.after && <span style={{ color: "var(--bm-red)", marginLeft: "6px", fontSize: "0.85em" }}>Max 60 minutes</span>}
              </label>

              <div className="bm-buffer-input-wrap">
                <input
                  type="number"
                  inputMode="numeric"
                  min={BUFFER_MIN}
                  step={5}
                  value={buffer.after}
                  onChange={(e) => handleBufferChange("after", e.target.value)}
                  onBlur={() => handleBufferBlur("after")}
                  onKeyDown={handleKeyDownBuffer}
                  onWheel={(e) => e.currentTarget.blur()}
                  className={`bm-select ${bufferErrors.after ? "bm-input-error" : ""}`}
                  placeholder="0"
                />
                <span className="bm-buffer-unit">Min</span>
              </div>
            </div>
          ) : (
            <div className="bm-field-display">{buffer.after} min{buffer.after === 1 ? "" : "s"} after meeting (wrap-up)</div>
          )}
        </div>
      </div>

      {/* Availability */}
      <div className="bm-section" ref={availabilitySectionRef} style={{borderTop: "1px solid var(--bm-border)", paddingTop: "15px"}}>
        <h3 className="bm-section-title">Availability</h3>
        <p className="bm-section-desc">
          Set the days & hours students can book 1:1 sessions with you
        </p>
       

        {isEditing ? (
          <>
            <div className="bm-availability-tabs">
              <button
                type="button"
                className={`bm-availability-tab ${availability.mode === "set-by-day" ? "active" : ""}`}
                onClick={() =>
                  setAvailability({ ...availability, mode: "set-by-day" })
                }
              >
                Set by Day
              </button>
              <button
                type="button"
                className={`bm-availability-tab ${availability.mode === "every-day" ? "active" : ""}`}
                onClick={() =>
                  setAvailability({ ...availability, mode: "every-day" })
                }
              >
                Same Everyday
              </button>
            </div>

            {availability.mode === "set-by-day" && (
              <div className="bm-availability-list">
                {DAYS.map((day) => (
                  <div
                    key={day}
                    className={`bm-availability-day ${availability.slots[day].timeSlots.length === 0 ? "unavailable" : ""}`}
                    style={{ alignItems: availability.slots[day].timeSlots.length > 0 ? "flex-start" : "center" }}
                  >
                    <div className="bm-day-toggle">
                      <button
                        type="button"
                        className={`bm-toggle-btn ${availability.slots[day].enabled ? "enabled" : ""}`}
                        onClick={() =>
                          handleAvailabilityChange(
                            day,
                            "enabled",
                            !availability.slots[day].enabled,
                          )
                        }
                        aria-label={`Toggle ${day} availability`}
                      >
                      <span className="bm-toggle-circle"></span>
                      </button>
                      <label className="bm-day-label">{day}</label>
                    </div>

                    {availability.slots[day].enabled ? (
                      <div className="bm-day-time-slots">
                        {availability.slots[day].timeSlots.map((slot) => {
                          const errorState = getTimeSlotErrorState(slot, availability.slots[day].timeSlots);
                          const hasFormatError = timePickerErrors.has(`${day}-${slot.id}-start`) || timePickerErrors.has(`${day}-${slot.id}-end`);
                          const slotMessage = hasFormatError
                            ? "Enter a valid time"
                            : errorState.overlap
                              ? "This time overlaps with another slot."
                              : errorState.hasError
                                ? "Choose an end time later than the start time."
                                : null;
                          return (
                          <div key={slot.id} className="bm-time-slot-wrapper">
                            <div className="bm-time-slot-row">
                              <TimePickerDropdown
                                value={slot.start}
                                onChange={(time) => handleTimeSlotChange(day, slot.id, "start", time)}
                                hasError={errorState.hasError || timePickerErrors.has(`${day}-${slot.id}-start`)}
                                onError={(hasError) => handleTimePickerError(`${day}-${slot.id}-start`, hasError)}
                              />
                              <span className="bm-time-separator">To</span>
                              <TimePickerDropdown
                                value={slot.end}
                                onChange={(time) => handleTimeSlotChange(day, slot.id, "end", time)}
                                hasError={errorState.hasError || timePickerErrors.has(`${day}-${slot.id}-end`)}
                                onError={(hasError) => handleTimePickerError(`${day}-${slot.id}-end`, hasError)}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveTimeSlot(day, slot.id)
                                }
                                className="bm-remove-slot-btn"
                                aria-label="Remove time slot"
                                style={{
                                  visibility: availability.slots[day].timeSlots.length > 1 ? "visible" : "hidden",
                                }}
                              >
                                <i className="fa-solid fa-xmark" />
                              </button>
                            </div>
                            {slotMessage && <p className="bm-slot-error">{slotMessage}</p>}
                          </div>
                        );
                        })}

                        <button
                          type="button"
                          onClick={() => handleAddTimeSlot(day)}
                          className="bm-add-time-slot-link"
                        >
                          Add Time Slot
                        </button>
                      </div>
                    ) : (
                      <span className="bm-unavailable">Unavailable</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {availability.mode === "every-day" && (
              <div className="bm-availability-everyday">
                <div className="bm-day-selector">
                  {DAYS.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleToggleDayForEveryDay(day)}
                      className={`bm-day-btn ${
                        availability.everyDay.selectedDays.includes(day)
                          ? "active"
                          : ""
                      }`}
                    >
                      {day.slice(0, 3).toUpperCase()}
                    </button>
                  ))}
                </div>

                <div className="bm-everyday-time-slots">
                  <p className="bm-everyday-label">
                    Hours (applied to every selected day)
                  </p>
                  {availability.everyDay.timeSlots.map((slot) => {
                    const errorState = getTimeSlotErrorState(slot, availability.everyDay.timeSlots);
                    const hasFormatError = timePickerErrors.has(`everyday-${slot.id}-start`) || timePickerErrors.has(`everyday-${slot.id}-end`);
                    const slotMessage = hasFormatError
                      ? "Enter a valid time"
                      : errorState.overlap
                        ? "This time overlaps with another slot."
                        : errorState.hasError
                          ? "Choose an end time later than the start time."
                          : null;
                    return (
                    <div key={slot.id} className="bm-time-slot-wrapper">
                      <div className="bm-time-slot-row">
                      <TimePickerDropdown
                        value={slot.start}
                        onChange={(time) => handleEveryDayTimeSlotChange(slot.id, "start", time)}
                        hasError={errorState.hasError || timePickerErrors.has(`everyday-${slot.id}-start`)}
                          onError={(hasError) => handleTimePickerError(`everyday-${slot.id}-start`, hasError)}
                      />
                      <span className="bm-time-separator">To</span>
                      <TimePickerDropdown
                        value={slot.end}
                        onChange={(time) => handleEveryDayTimeSlotChange(slot.id, "end", time)}
                        hasError={errorState.hasError || timePickerErrors.has(`everyday-${slot.id}-end`)}
                          onError={(hasError) => handleTimePickerError(`everyday-${slot.id}-end`, hasError)}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveEveryDayTimeSlot(slot.id)}
                        className="bm-remove-slot-btn"
                        aria-label="Remove time slot"
                        style={{
                          visibility: availability.everyDay.timeSlots.length > 1 ? "visible" : "hidden",
                        }}
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                      </div>
                      {slotMessage && <p className="bm-slot-error">{slotMessage}</p>}
                    </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={handleAddEveryDayTimeSlot}
                    className="bm-add-time-slot-link"
                  >
                    Add Time Slot
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bm-availability-view">
            {DAYS.map((day) => {
              const daySlots = availability.slots[day];

              let timeRanges = "Unavailable";
              if (daySlots.enabled) {
                timeRanges = daySlots.timeSlots
                  .map((slot) => {
                    const startTime =
                      TIME_SLOTS.find((t) => t.value === slot.start)?.label ||
                      convertTo12Hour(slot.start);
                    const endTime =
                      TIME_SLOTS.find((t) => t.value === slot.end)?.label ||
                      convertTo12Hour(slot.end);
                    return `${startTime} - ${endTime}`;
                  })
                  .join(", ");
              }

              return (
                <div key={day} className="bm-availability-view-row">
                  <span className="bm-availability-day-name">{day}</span>
                  <span
                    className={`bm-availability-time-range ${!daySlots.enabled ? "bm-unavailable-text" : ""}`}
                  >
                    {timeRanges}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Save Button */}
      {/* Removed from here - now in header */}
    </div>
  );
};

export default OneOnOneSession;
